import { Link } from 'react-router-dom';

interface Event {
  id: string;
  title: string;
  category: string;
  start_date: string;
  location_type: string;
  location_city: string | null;
  location_country: string | null;
  price: number | null;
  currency: string | null;
  cover_image: string | null;
  registrations?: { count: number }[];
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', JPY: '¥',
  NGN: '₦', GHS: 'GH₵', KES: 'KSh', ZAR: 'R', UGX: 'USh',
  TZS: 'TSh', RWF: 'FRw', ETB: 'Br', EGP: 'E£', MAD: 'MAD',
  XOF: 'CFA', XAF: 'FCFA', BRL: 'R$', MXN: 'MX$', INR: '₹',
  CNY: '¥', SGD: 'S$', AED: 'د.إ', SAR: '﷼',
};

function formatPrice(price: number | null, currency: string | null): string {
  if (!price || price === 0) return 'Free';
  const code = (currency || 'USD').toUpperCase();
  const symbol = CURRENCY_SYMBOLS[code] || code;
  const formatted = price % 1 === 0 ? price.toLocaleString() : price.toFixed(2);
  // For codes that are words (not symbols), show "CODE amount"
  const isWordCode = symbol === code;
  return isWordCode ? `${code} ${formatted}` : `${symbol}${formatted}`;
}

interface EventCardProps {
  event: Event;
  isRegistered?: boolean;
}

const EventCard = ({ event, isRegistered = false }: EventCardProps) => {
  // Format date from start_date
  const eventDate = new Date(event.start_date);
  const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const month = monthNames[eventDate.getMonth()];
  const day = eventDate.getDate();

  // Get location display
  const location = event.location_type === 'online' 
    ? 'Online Event' 
    : event.location_city || event.location_country || 'Location TBA';

  // Get attendee count
  const attendees = event.registrations?.[0]?.count || 0;

  // Category color mapping
  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Music': 'text-purple-700',
      'Technology': 'text-blue-700',
      'Business': 'text-orange-700',
      'Sports': 'text-green-700',
      'Arts': 'text-pink-700',
      'Food': 'text-red-700',
    };
    return colors[category] || 'text-gray-700';
  };

  return (
    <Link
      to={`/event/${event.id}`}
      className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border border-gray-100 cursor-pointer"
    >
      <div className="relative h-56 overflow-hidden">
        <img 
          src={event.cover_image || 'https://public.readdy.ai/ai/img_res/3b464ffb3e80d64e54e7263c75f66da2.jpg'} 
          alt={event.title}
          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4">
          <div className="bg-white rounded-lg px-3 py-2 shadow-lg">
            <p className="text-2xl font-bold text-gray-900">{day}</p>
            <p className="text-xs font-semibold text-gray-600">{month}</p>
          </div>
        </div>
        <div className="absolute top-4 right-4 flex flex-col items-end gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-white/90 backdrop-blur-sm whitespace-nowrap ${getCategoryColor(event.category)}`}>
            {event.category}
          </span>
          {isRegistered && (
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500 text-white whitespace-nowrap flex items-center gap-1 shadow-lg">
              <i className="ri-checkbox-circle-fill text-xs"></i>
              Registered
            </span>
          )}
        </div>
        <div className="absolute bottom-4 left-4">
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-900/80 text-white backdrop-blur-sm whitespace-nowrap">
            <i className={`${event.location_type === 'online' ? 'ri-global-line' : 'ri-map-pin-line'} mr-1`}></i>
            {event.location_type === 'online' ? 'Online' : 'In-Person'}
          </span>
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
          {event.title}
        </h3>
        
        <div className="flex items-center gap-2 mb-4">
          <i className="ri-map-pin-line text-gray-500"></i>
          <p className="text-gray-600 text-sm">{location}</p>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div>
            <div>
              <p className="text-2xl font-bold text-teal-600">
                {formatPrice(event.price, event.currency)}
              </p>
              {event.price && event.price > 0 && event.currency && event.currency.toUpperCase() !== 'USD' && (
                <p className="text-xs text-gray-400 mt-0.5">{event.currency.toUpperCase()}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <i className="ri-group-line"></i>
              <span>{attendees}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default EventCard;
